import { useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import api, { formatApiErrorDetail } from "../lib/api";
import { useAuth } from "../lib/auth";
import { Input } from "../components/ui/input";
import { Button } from "../components/ui/button";
import { Label } from "../components/ui/label";
import { Card } from "../components/ui/card";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Briefcase } from "lucide-react";
import { toast } from "sonner";
import { CATEGORIES, CATEGORY_ORDER, PRIORITY_CITIES, OTHER_CITIES } from "../lib/categories";

const LOGO_URL =
  "https://customer-assets.emergentagent.com/wingman/7e1b41d7-cb47-4627-a363-d0e2bc80c154/attachments/355f24cbb44947998348bd262690697f_image.png";

export default function OwnerSignupPage() {
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const preselectedPlan = params.get("plan") || "basic";
  const { refresh } = useAuth();
  const [form, setForm] = useState({
    name: "",
    email: "",
    password: "",
    business_name: "",
    business_category: "restaurant",
    business_city: "N'Djamena",
    phone: "",
  });
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const upd = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));
  const updVal = (k) => (v) => setForm((f) => ({ ...f, [k]: v }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    if (form.password.length < 6) {
      setError("Le mot de passe doit comporter au moins 6 caractères");
      return;
    }
    setLoading(true);
    try {
      await api.post("/owner/signup", form);
      await refresh();
      toast.success("Bienvenue parmi les annonceurs Kanama !");
      // Redirect to pricing if a paid plan was preselected, else dashboard
      if (preselectedPlan && preselectedPlan !== "basic") {
        navigate(`/tarifs?next=${preselectedPlan}`);
      } else {
        navigate("/annonceur");
      }
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || "Erreur");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-[80vh] flex items-center justify-center px-5 py-12">
      <Card className="w-full max-w-2xl p-7 sm:p-9 rounded-2xl border-brand-line bg-white shadow-[0_8px_32px_rgba(45,42,38,0.06)]" data-testid="owner-signup-page">
        <Link to="/" className="flex items-center gap-2.5 mb-7">
          <img src={LOGO_URL} alt="Kanama" className="h-10 w-10 object-contain" />
          <span className="font-heading font-bold text-xl text-brand-ink">Kanama</span>
        </Link>

        <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-brand-gold/15 text-brand-goldDark text-xs uppercase tracking-[0.18em] font-bold mb-4">
          <Briefcase className="w-3.5 h-3.5" />
          Espace annonceur
        </div>
        <h1 className="font-heading text-2xl sm:text-3xl font-semibold text-brand-ink mb-2 tracking-tight">
          Devenir annonceur Kanama
        </h1>
        <p className="text-sm text-brand-inkSoft mb-7">
          Créez votre compte pour publier votre commerce ou service sur Kanama. La publication d'une annonce
          en formule <strong>Basique</strong> est gratuite, avec une commission de <strong>10 %</strong> sur
          chaque article vendu. Pour ne payer aucune commission, choisissez un abonnement Premium :
          <strong> 50 000 FCFA / mois</strong> ou <strong>500 000 FCFA / an</strong>.
        </p>

        <form onSubmit={handleSubmit} className="space-y-4" data-testid="owner-signup-form">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <Label>Votre nom *</Label>
              <Input required autoComplete="name" enterKeyHint="next" value={form.name} onChange={upd("name")} className="mt-1.5 rounded-xl h-12 text-base" data-testid="owner-input-name" />
            </div>
            <div>
              <Label>E-mail *</Label>
              <Input required type="email" autoComplete="email" inputMode="email" enterKeyHint="next" value={form.email} onChange={upd("email")} className="mt-1.5 rounded-xl h-12 text-base" data-testid="owner-input-email" />
            </div>
            <div>
              <Label>Mot de passe *</Label>
              <Input required type="password" autoComplete="new-password" enterKeyHint="next" value={form.password} onChange={upd("password")} className="mt-1.5 rounded-xl h-12 text-base" placeholder="Au moins 6 caractères" data-testid="owner-input-password" />
            </div>
            <div>
              <Label>Téléphone *</Label>
              <Input required type="tel" autoComplete="tel" inputMode="tel" enterKeyHint="next" value={form.phone} onChange={upd("phone")} className="mt-1.5 rounded-xl h-12 text-base" placeholder="+235 ..." data-testid="owner-input-phone" />
            </div>
            <div className="sm:col-span-2">
              <Label>Nom de votre commerce *</Label>
              <Input required autoComplete="organization" enterKeyHint="next" value={form.business_name} onChange={upd("business_name")} className="mt-1.5 rounded-xl h-12 text-base" data-testid="owner-input-biz-name" />
            </div>
            <div>
              <Label>Catégorie d'activité *</Label>
              <Select value={form.business_category} onValueChange={updVal("business_category")}>
                <SelectTrigger className="mt-1.5 rounded-xl h-11" data-testid="owner-input-biz-cat">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {CATEGORY_ORDER.map((k) => (
                    <SelectItem key={k} value={k}>{CATEGORIES[k].label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Ville *</Label>
              <Select value={form.business_city} onValueChange={updVal("business_city")}>
                <SelectTrigger className="mt-1.5 rounded-xl h-11" data-testid="owner-input-biz-city">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {PRIORITY_CITIES.map((c) => (
                    <SelectItem key={c} value={c}>{c}</SelectItem>
                  ))}
                  {OTHER_CITIES.map((c) => (
                    <SelectItem key={c} value={c}>{c}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
          {error && (
            <div className="text-sm text-destructive bg-red-50 border border-red-200 rounded-xl p-3" data-testid="owner-signup-error">{error}</div>
          )}
          <Button
            type="submit"
            disabled={loading}
            className="w-full h-11 rounded-full bg-brand-terracotta hover:bg-brand-terracottaHover text-white font-semibold"
            data-testid="owner-signup-submit"
          >
            {loading ? "Création..." : "Créer mon compte annonceur"}
          </Button>
          <p className="text-xs text-brand-inkMuted text-center">
            En vous inscrivant, vous acceptez les{" "}
            <span className="underline">conditions d'utilisation</span> et la{" "}
            <span className="underline">politique de confidentialité</span> de Kanama.
          </p>
          <p className="text-sm text-brand-inkSoft text-center pt-2">
            Déjà inscrit ?{" "}
            <Link to="/login" className="text-brand-terracotta font-semibold hover:underline">
              Connectez-vous
            </Link>
          </p>
        </form>
      </Card>
    </div>
  );
}
